import { useAuth } from '@/auth/Auth';
import PaymentMethodSelector from '@/components/shared/PaymentMethodSelector/PaymentMethodSelector';
import { Input } from '@/components/shared/Input/Input';
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect';
import { MultiComboBox } from '@/components/shared/MultiComboBox/MultiComboBox';
import StatusSelector from '@/components/shared/StatusSelector/StatusSelector';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker';
import { OffersService } from '@/services/offers.service';
import { SellsService } from '@/services/sells.service';
import useCurrencyStore from '@/store/useCurrencyStore';
import useModalStore from '@/store/useModalStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  SUBSCRIPTION_STATUSES,
} from '../hooks/useSubscriptions';
import type { SubscriptionDateAxis } from '@/services/subscriptions.service';
import { Label } from '@/components/shared/Label/Label';

const SUBSCRIPTION_PAYMENT_METHOD_TYPES = new Set(['credit_card', 'bank_slip']);

const FilterSchema = z.object({
  products: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  offers: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  affiliates: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  coproducers: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  payment_methods: z.array(z.string()).optional(),
  status: z.array(z.string()).optional(),
  src: z.string().optional(),
  utm_source: z.string().optional(),
  utm_medium: z.string().optional(),
  utm_campaign: z.string().optional(),
  utm_content: z.string().optional(),
  utm_term: z.string().optional(),
});

type FilterFormData = z.infer<typeof FilterSchema>;

export default function FilterSubscriptions({
  onFilter,
  filterValues,
  dateAxis,
  dateRange,
  onDateAxisChange,
  onDateRangeChange,
}: {
  filterValues: FilterFormData;
  onFilter: (data: FilterFormData) => void;
  dateAxis: SubscriptionDateAxis;
  dateRange: DateRange | undefined;
  onDateAxisChange: (axis: SubscriptionDateAxis) => void;
  onDateRangeChange: (range: DateRange | undefined) => void;
}) {
  const { t } = useTranslation(['signatures', 'sales', 'common']);
  const { closeModal } = useModalStore();
  const { authData } = useAuth();
  const { currency } = useCurrencyStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
  } = useForm<FilterFormData>({
    resolver: zodResolver(FilterSchema),
    defaultValues: {
      products: filterValues?.products || [],
      offers: filterValues?.offers || [],
      affiliates: filterValues?.affiliates || [],
      coproducers: filterValues?.coproducers || [],
      payment_methods: filterValues?.payment_methods || [],
      status: filterValues?.status?.length
        ? filterValues.status
        : [...SUBSCRIPTION_STATUSES],
      src: filterValues?.src || undefined,
      utm_source: filterValues?.utm_source || undefined,
      utm_medium: filterValues?.utm_medium || undefined,
      utm_campaign: filterValues?.utm_campaign || undefined,
      utm_content: filterValues?.utm_content || undefined,
      utm_term: filterValues?.utm_term || undefined,
    },
  });

  const defaultValues = watch();

  const onSubmit = (data: FilterFormData) => {
    onFilter(data);
    closeModal('filter-subscriptions');
  };

  const handleSelectAllStatus = () => {
    const current = defaultValues.status ?? [];
    const all = [...SUBSCRIPTION_STATUSES];
    setValue('status', current.length === all.length ? [] : all);
  };

  const fetchProductOptions = async (query?: string) => {
    return await SellsService.getSoldProducts(query, currency);
  };

  const fetchOfferOptions = async (query?: string) => {
    return (await OffersService.getAllOffers(query)) || [];
  };

  const fetchAffiliatesOptions = async (query?: string) => {
    return await SellsService.getPartnersByRole('affiliate', query);
  };

  const fetchCoProducersOptions = async (query?: string) => {
    return await SellsService.getPartnersByRole('coproducer', query);
  };

  return (
    <section className="h-full flex relative flex-col justify-between">
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">{t('sales:filters')}</SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex relative flex-col gap-4 px-4 md:px-6">
          <CustomSelect
            label={t('sales:date_type_label')}
            placeholder={t('sales:date_type_label')}
            value={dateAxis}
            onValueChange={(value) =>
              onDateAxisChange(value as SubscriptionDateAxis)
            }
            options={[
              { value: 'started', label: t('date_axis.started') },
              { value: 'billing', label: t('date_axis.billing') },
              { value: 'canceled', label: t('date_axis.canceled') },
            ]}
          />

          <div className="space-y-2">
            <Label>{t('sales:date_range_label')}</Label>
            <DateRangePicker
              defaultValue={dateRange}
              onChange={onDateRangeChange}
              widthFull
            />
          </div>

          <MultiComboBox
            label={t('sales:products')}
            placeholder={t('sales:products_placeholder')}
            fetchOptions={fetchProductOptions}
            values={watch('products') ?? []}
            onChange={(value) => {
              setValue('products', value);
              clearErrors('products');
            }}
            helperText={errors.products?.message}
            queryKey="sold-products-subscriptions"
            returnFullObject
            cypressName="multicombobox-products"
            selectAllOptions
            newParams={[currency]}
          />

          <MultiComboBox
            label={t('sales:offer')}
            placeholder={t('sales:offer_placeholder')}
            fetchOptions={fetchOfferOptions}
            values={watch('offers') ?? []}
            onChange={(values) => {
              setValue('offers', values);
              clearErrors('offers');
            }}
            helperText={errors.offers?.message}
            queryKey="offers-subscriptions"
            returnFullObject
            cypressName="multicombobox-offers"
            selectAllOptions
          />

          <MultiComboBox
            label={t('sales:affiliate')}
            placeholder={t('sales:affiliate_placeholder')}
            fetchOptions={fetchAffiliatesOptions}
            values={watch('affiliates') ?? []}
            onChange={(value) => {
              setValue('affiliates', value);
              clearErrors('affiliates');
            }}
            helperText={errors.affiliates?.message}
            queryKey="affiliates-subscriptions"
            returnFullObject
            cypressName="multicombobox-affiliates"
            selectAllOptions
          />

          <MultiComboBox
            label={t('sales:co_producer')}
            placeholder={t('sales:co_producer_placeholder')}
            fetchOptions={fetchCoProducersOptions}
            values={watch('coproducers') ?? []}
            onChange={(value) => {
              setValue('coproducers', value);
              clearErrors('coproducers');
            }}
            helperText={errors.coproducers?.message}
            queryKey="coproducers-subscriptions"
            returnFullObject
            cypressName="multicombobox-coproducers"
            selectAllOptions
          />

          <Separator className="my-4" />

          <PaymentMethodSelector
            variant="compact"
            availableMethods={(authData?.available_payment_methods ?? []).filter(
              (method) => SUBSCRIPTION_PAYMENT_METHOD_TYPES.has(method.type),
            )}
            selectedMethods={defaultValues.payment_methods ?? []}
            onChange={(methods) => {
              setValue('payment_methods', methods);
              clearErrors('payment_methods');
            }}
          />

          <Separator className="my-4" />

          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">
                {t('subscription_status')}
              </h2>
              <span
                className="text-sm font-medium text-primary hover:text-primary/80 cursor-pointer"
                onClick={handleSelectAllStatus}
              >
                {t('sales:select_all')}
              </span>
            </div>
            <StatusSelector
              options={SUBSCRIPTION_STATUSES.map((status) => ({
                value: status,
                label: t(`status.${status}`),
              }))}
              selectedOptions={defaultValues.status ?? []}
              onChange={(statuses) => {
                setValue('status', statuses);
                clearErrors('status');
              }}
              variant="compact"
            />
          </div>

          <Separator className="my-4" />

          <div className="space-y-4 mb-24">
            <h2 className="text-lg font-semibold">{t('sales:url_parameters')}</h2>
            <Input
              {...register('src')}
              label={t('sales:src')}
              placeholder={t('sales:src_placeholder')}
              helperText={errors.src?.message}
              data-cy="input-src"
            />
            <Input
              {...register('utm_source')}
              label={t('sales:utm_source')}
              placeholder={t('sales:utm_source_placeholder')}
              helperText={errors.utm_source?.message}
              data-cy="input-utm-source"
            />
            <Input
              {...register('utm_medium')}
              label={t('sales:utm_medium')}
              placeholder={t('sales:utm_medium_placeholder')}
              helperText={errors.utm_medium?.message}
              data-cy="input-utm-medium"
            />
            <Input
              {...register('utm_campaign')}
              label={t('sales:utm_campaign')}
              placeholder={t('sales:utm_campaign_placeholder')}
              helperText={errors.utm_campaign?.message}
              data-cy="input-utm-campaign"
            />
            <Input
              {...register('utm_content')}
              label={t('sales:utm_content')}
              placeholder={t('sales:utm_content_placeholder')}
              helperText={errors.utm_content?.message}
              data-cy="input-utm-content"
            />
            <Input
              {...register('utm_term')}
              label={t('sales:utm_term')}
              placeholder={t('sales:utm_term_placeholder')}
              helperText={errors.utm_term?.message}
              data-cy="input-utm-term"
            />
          </div>
        </section>

        <SheetFooter className="flex z-50 sticky bottom-0 bg-[#09090b] w-full border-t border-t-colorBorder p-4 items-end mt-4 justify-end">
          <div className="flex w-full z-50 gap-2 justify-end items-end">
            <Button
              variant="ghost"
              className="w-full !text-colorText md:max-w-[118px]"
              type="button"
              onClick={() => closeModal('filter-subscriptions')}
            >
              {t('sales:cancel')}
            </Button>
            <Button
              variant="default"
              className="w-full md:max-w-fit"
              type="submit"
              data-cy="apply-filters"
            >
              {t('sales:apply_filters')}
            </Button>
          </div>
        </SheetFooter>
      </form>
    </section>
  );
}
